import React, { useState } from 'react';
import { Alert, Button, Card, Form, Modal, Table } from 'react-bootstrap';
import dayjs from 'dayjs';
import Flex from 'components/common/Flex';
import IconButton from 'components/common/IconButton';
import Skeleton from 'components/common/Skeleton';
import SoftBadge from 'components/common/SoftBadge';
import { FORMAT_DATE_TIME_II } from 'constants/index';
import { useDriversContext } from '../../DriversContext';
import { RESTRICTION_TYPES } from '../driver.constant';
import LockSourceBadge from './LockSourceBadge';

// FraudCheck is the operator's view of the payment velocity check: what is
// blocking this driver, which rule trip caused it, and the actions that clear or
// apply a block.
//
// The data comes from the shared instance on the context, fetched when the
// driver was opened, so this tab and the header badge never disagree.
export default function FraudCheck() {
  const { data, driverId, paymentVelocity, onUpdateStatusSuccess } = useDriversContext();
  const {
    restrictions,
    incidents,
    releaseLogs,
    loading,
    acting,
    forcePrompt,
    release,
    lock,
    dismissForcePrompt
  } = paymentVelocity || {};

  const [note, setNote] = useState('');
  const [selectedTypes, setSelectedTypes] = useState([]);

  const { fleet_id, company_id } = data;
  const userId = driverId || data.id;

  // Refetching the driver is not redundant with the velocity refresh that has
  // already run: releasing or applying an account block moves users.status, and
  // the header Status and the row in the drivers table both read the driver
  // record, not velocity state. Without this the same screen contradicts itself.
  const onAfterAction = () => {
    setNote('');
    setSelectedTypes([]);
    onUpdateStatusSuccess({ driver_id: userId, fleet_id, company_id });
  };

  const onToggleType = value => {
    setSelectedTypes(current =>
      current.includes(value) ? current.filter(type => type !== value) : [...current, value]
    );
  };

  // Nothing ticked means every block. The button label says which it will be, so
  // the operator is not left guessing what an empty selection does.
  const onRelease = (force = false) =>
    release({ userId, types: selectedTypes, force, note, onDone: onAfterAction });

  const onLock = () => lock({ userId, types: selectedTypes, note, onDone: onAfterAction });

  // The account block is the only one that removes sign-in and the only one that
  // emails the driver, so the operator is told before they confirm.
  const willEmailDriver = selectedTypes.includes('account');

  const activeRestrictions = (restrictions?.restrictions || []).filter(item => item.is_active);
  const incidentItems = incidents?.items || [];
  const releaseLogItems = releaseLogs?.items || [];

  if (!paymentVelocity || (!loading && !restrictions)) {
    return (
      <div className="fs--1 text-600">
        Fraud check data could not be loaded for this driver. Reopen the driver to try again.
      </div>
    );
  }

  return (
    <div className="fs--1">
      <Card className="mb-3">
        <Card.Header className="p-2">
          <Flex alignItems="center" justifyContent="between">
            <b>Current state</b>
            <LockSourceBadge restrictions={restrictions} />
          </Flex>
        </Card.Header>
        <Card.Body className="p-2">
          <Skeleton loading={loading} xs={12}>
            {activeRestrictions.length === 0 ? (
              <span className="text-600">No active restriction.</span>
            ) : (
              <Table size="sm" bordered className="mb-0">
                <thead>
                  <tr>
                    <th>Type</th>
                    <th>Source</th>
                    <th>Reason</th>
                    <th>Since</th>
                  </tr>
                </thead>
                <tbody>
                  {activeRestrictions.map(item => (
                    <tr key={item.id}>
                      <td>{item.type}</td>
                      <td>
                        <SoftBadge bg={item.is_manual ? 'warning' : 'danger'} pill>
                          {item.is_manual ? 'Admin' : 'Fraud rule'}
                        </SoftBadge>
                      </td>
                      <td>{item.reason_code}</td>
                      <td>{dayjs(item.starts_at).format(FORMAT_DATE_TIME_II)}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            )}
          </Skeleton>
        </Card.Body>
      </Card>

      <Card className="mb-3">
        <Card.Header className="p-2">
          <b>Rule trips</b>
        </Card.Header>
        <Card.Body className="p-0">
          <Skeleton loading={loading} xs={12}>
            {incidentItems.length === 0 ? (
              <div className="p-2 text-600">No rule has tripped for this driver.</div>
            ) : (
              <div className="table-responsive">
                <Table size="sm" bordered className="mb-0">
                  <thead>
                    <tr>
                      <th>When</th>
                      <th>Rule</th>
                      <th>Metric</th>
                      {/* Observed against the threshold and window is what separates a near
                          miss from a landslide. */}
                      <th>Observed</th>
                      <th>Window</th>
                      <th>Stripe</th>
                    </tr>
                  </thead>
                  <tbody>
                    {incidentItems.map(item => (
                      <tr key={item.id}>
                        <td>{dayjs(item.created_at).format(FORMAT_DATE_TIME_II)}</td>
                        <td>{item.rule_code}</td>
                        <td>{item.metric}</td>
                        <td>
                          <b>{item.count}</b> / {item.threshold}
                        </td>
                        <td>{item.window_seconds}s</td>
                        <td>
                          {/* Whether a Stripe side effect failed, not why. The
                              message itself stays server-side. */}
                          {item.stripe_error ? (
                            <SoftBadge bg="warning" pill>
                              failed
                            </SoftBadge>
                          ) : (
                            '-'
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            )}
          </Skeleton>
        </Card.Body>
      </Card>

      {/* Action history is the only place an operator's note surfaces again: a
          restriction row has no field for one, and a forced release that matched
          no rows leaves no restriction behind at all. Without this card the note
          box writes to somewhere nobody can read. */}
      <Card className="mb-3">
        <Card.Header className="p-2">
          <b>Action history</b>
        </Card.Header>
        <Card.Body className="p-0">
          <Skeleton loading={loading} xs={12}>
            {releaseLogItems.length === 0 ? (
              <div className="p-2 text-600">No admin has locked or recovered this driver yet.</div>
            ) : (
              <div className="table-responsive">
                <Table size="sm" bordered className="mb-0">
                  <thead>
                    <tr>
                      <th>When</th>
                      <th>Who</th>
                      <th>Action</th>
                      <th>Types</th>
                      <th>Note</th>
                    </tr>
                  </thead>
                  <tbody>
                    {releaseLogItems.map(item => (
                      <tr key={item.id}>
                        <td>{dayjs(item.created_at).format(FORMAT_DATE_TIME_II)}</td>
                        {/* An admin removed since leaves no name, so the id is
                            what is left to trace them by. */}
                        <td>{item.actor_name || item.actor_id}</td>
                        <td>
                          {item.action}
                          {item.forced && (
                            <SoftBadge bg="danger" pill className="ms-1">
                              forced
                            </SoftBadge>
                          )}
                        </td>
                        {/* No types on a release means every block went, so a
                            dash would read as the opposite of the truth. On a
                            lock it means the reverse: the types were already
                            blocked and nothing was applied. */}
                        <td>
                          {item.restriction_types?.length > 0
                            ? item.restriction_types.join(', ')
                            : item.action === 'release'
                            ? 'All'
                            : '-'}
                        </td>
                        <td>{item.note || '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            )}
          </Skeleton>
        </Card.Body>
      </Card>

      <Card className="mb-3">
        <Card.Header className="p-2">
          <b>Actions</b>
        </Card.Header>
        <Card.Body className="p-2">
          <Form.Group className="mb-2">
            <Form.Label className="mb-1">Note</Form.Label>
            <Form.Control
              size="sm"
              as="textarea"
              rows={2}
              value={note}
              maxLength={255}
              placeholder="Why are you doing this? Recorded on the audit trail."
              onChange={event => setNote(event.target.value)}
            />
            <Form.Text className="text-600">
              Shown in Action history above, against your name.
            </Form.Text>
          </Form.Group>

          <div className="mb-2">
            <Form.Label className="mb-1">
              Restriction types{' '}
              <span className="text-600">— required to apply, optional to recover</span>
            </Form.Label>
            {RESTRICTION_TYPES.map(type => (
              <Form.Check
                key={type.value}
                type="checkbox"
                id={`velocity-type-${type.value}`}
                label={
                  <>
                    {type.label} <span className="text-600">— {type.hint}</span>
                  </>
                }
                checked={selectedTypes.includes(type.value)}
                onChange={() => onToggleType(type.value)}
              />
            ))}
          </div>

          {willEmailDriver && (
            <Alert variant="info" className="py-2 mb-2">
              Locking the account emails the driver a suspension notice.
            </Alert>
          )}

          <Flex className="gap-2 flex-wrap">
            <IconButton
              size="sm"
              icon="unlock"
              variant="primary"
              loading={acting}
              disabled={acting}
              onClick={() => onRelease(false)}
            >
              <span className="ms-1">
                {selectedTypes.length > 0 ? 'Recover selected' : 'Recover all'}
              </span>
            </IconButton>
            <IconButton
              size="sm"
              icon="lock"
              variant="outline-danger"
              loading={acting}
              disabled={acting || selectedTypes.length === 0}
              onClick={onLock}
            >
              <span className="ms-1">Apply block</span>
            </IconButton>
          </Flex>
          <div className="mt-2 text-600">
            Recover clears blocks whether a fraud rule or an admin applied them. Tick types to
            recover just those; leave them all unticked to clear everything.
          </div>
        </Card.Body>
      </Card>

      {/* The API refuses to recover a driver no rule blocked. Confirming here is
          what turns a wrong one-click unlock into a deliberate one. */}
      <Modal centered show={Boolean(forcePrompt)} onHide={dismissForcePrompt}>
        <Modal.Header closeButton>
          <Modal.Title className="fs-0">Unlock anyway?</Modal.Title>
        </Modal.Header>
        <Modal.Body className="fs--1">
          <p>{forcePrompt}</p>
          <p className="mb-0">
            This driver is not held by a fraud rule, so there is nothing for the fraud check to
            release. Unlocking will return them to active regardless.
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button size="sm" variant="secondary" onClick={dismissForcePrompt}>
            Cancel
          </Button>
          <Button size="sm" variant="danger" disabled={acting} onClick={() => onRelease(true)}>
            Unlock anyway
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}
